import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { OrganizationBubble } from '@/components/user/OrganizationBubble';
import { RoleBadge } from '@/components/user/RoleBadge';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import {
    ActiveRoleAssignmentResponse,
    getAllRoleAssignments,
    type RoleAssignmentsContainer,
} from '@pillar-portal/api-types';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';

interface RoleAssignmentRow {
    role_assignment_id: number;
    role_assignment_uuid: string;
    role: string;
    organization_id: number | null;
    organization_name?: string;
    isActive: boolean;
    isDefault: boolean;
}

interface RoleAssignmentsTableProps {
    user: RoleAssignmentsContainer;
}

export function RoleAssignmentsTable({ user }: RoleAssignmentsTableProps) {
    const log = createComponentLogger('RoleAssignmentsTable');
    const queryClient = useQueryClient();
    const setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void = useAuthStore(
        (state) => state.setActiveRoleAssignmentId
    );

    const setActiveRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setActiveRoleAssignment,
        onSuccess: () => {
            // Avoid carrying stale pkey state while the active-role query refetches.
            setActiveRoleAssignmentId(null);
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['activeRoleAssignment'] }),
                (err) => {
                    log.error('Error invalidating active role assignment', err);
                }
            );
            // Switching active role assignment changes authorization context; force refetch.
            runInBackground(queryClient.cancelQueries(), (err) => {
                log.error('Error cancelling queries after role switch', err);
            });
            runInBackground(queryClient.resetQueries({ predicate: () => true }), (err) => {
                log.error('Error resetting queries after role switch', err);
            });
        },
    });

    const setDefaultRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setDefaultRoleAssignment,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
        },
    });

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000,
    });
    useEffect(() => {
        const nextActiveRoleAssignmentId: number | null =
            getAllRoleAssignments(user).find(
                (roleAssignment) =>
                    roleAssignment.role_assignment_uuid ===
                    activeRoleAssignment?.role_assignment_uuid
            )?.role_assignment_id ?? null;
        setActiveRoleAssignmentId(nextActiveRoleAssignmentId);
    }, [activeRoleAssignment?.role_assignment_uuid, setActiveRoleAssignmentId, user]);

    // Transform role_assignments into table rows with status flags
    const roleAssignments = getAllRoleAssignments(user);
    const rows: RoleAssignmentRow[] = roleAssignments.map((ra) => {
        if (ra.role_assignment_uuid === undefined) {
            throw new Error('role_assignment_uuid is required for role assignment operations');
        }
        return {
            role_assignment_id: ra.role_assignment_id,
            role_assignment_uuid: ra.role_assignment_uuid,
            role: ra.role,
            organization_id: ra.organization_id,
            ...(ra.organization_name && { organization_name: ra.organization_name }),
            isActive:
                activeRoleAssignment?.role_assignment_uuid === ra.role_assignment_uuid,
            isDefault: ra.role_assignment_id === user.default_role_assignment?.role_assignment_id,
        };
    });

    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const showSetActive: boolean = !row.isActive;
                const showSetDefault: boolean = !row.isDefault;

                return (
                    <div
                        key={row.role_assignment_id}
                        className="rounded border p-3 md:grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start md:gap-4"
                    >
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Organization</div>
                            {row.organization_id === null ? (
                                <span className="text-muted-foreground">-</span>
                            ) : (
                                <OrganizationBubble
                                    organizationName={row.organization_name || null}
                                    isDefault={row.isDefault}
                                />
                            )}
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Role</div>
                            <RoleBadge role={row.role} />
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Status</div>
                            {row.organization_id === null ? (
                                <span className="text-muted-foreground">-</span>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    {row.isActive && (
                                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-blue-500 text-white border-blue-600">
                                            Active
                                        </span>
                                    )}
                                    {row.isDefault && (
                                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 border-blue-300">
                                            Default
                                        </span>
                                    )}
                                    {!row.isActive && !row.isDefault && (
                                        <span className="text-muted-foreground">-</span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex flex-col gap-1 md:mt-0">
                            {showSetActive && (
                                <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs bg-blue-500 hover:bg-blue-600 text-white"
                                    onClick={() => {
                                        setActiveRoleAssignmentMutation.mutate(
                                            row.role_assignment_uuid
                                        );
                                    }}
                                    disabled={setActiveRoleAssignmentMutation.isPending}
                                >
                                    Set active
                                </Button>
                            )}
                            {showSetDefault && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                                    onClick={() => {
                                        setDefaultRoleAssignmentMutation.mutate(
                                            row.role_assignment_uuid
                                        );
                                    }}
                                    disabled={setDefaultRoleAssignmentMutation.isPending}
                                >
                                    Set default
                                </Button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
